/**
 * Area `caller-protection` of the console gateway — over `apps/api/src/routes/caller-protection/` (design §40.5,
 * §40.19.1). Owned by plan-029 unit **WAPP-PROT**: the VoiceAgent's caller protection — the per-caller limits, the
 * withheld-number refusal, the silent-start end and the longest conversation — read and saved with If-Match on its
 * revision (ADR 0081, proposed; D-2, D-5).
 *
 * ```
 *   readVoiceAgentCallerProtection     `getVoiceAgentCallerProtection`   GET /v1/agents/{id}/caller-protection   ETag = revision
 *   changeVoiceAgentCallerProtection   `setVoiceAgentCallerProtection`   PUT /v1/agents/{id}/caller-protection   If-Match required
 * ```
 *
 * A REFUSAL KEEPS ITS DETAIL. A broken rule answers `400 validation_failed` with `error.detail.code`
 * (`per_day_below_per_hour`, `silent_start_not_offered`, …); the generated `ApiError` drops `detail`, so every party
 * route of this console raises {@link PartyRefusedError} (an `ApiError`, the same code, status and field) with the
 * detail read, and the panels word the rule in the portal's language. The blocked-number and access-list slices use
 * the same class.
 *
 * The register is EMPTY: both operations are in the assembled spec. The sandbox half is `../sandbox/caller-protection.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** The silent-start ends the api accepts, in seconds (design §40.19.1). */
export const SILENT_START_END_SECOND = [10, 20, 30, 60] as const;
/** The longest conversations the api accepts, in minutes (design §40.19.1). */
export const LONGEST_CONVERSATION_MINUTE = [5, 10, 15, 30, 60, 120] as const;
/** The per-caller limits' ranges (design §40.19.1; D-5). */
export const PER_HOUR_MOST = 100;
export const PER_DAY_MOST = 500;

/** A VoiceAgent's caller protection (`CallerProtection`). `null` is "off" for every optional setting. */
export interface CallerProtectionView {
  readonly perHour: number | null;
  readonly perDay: number | null;
  readonly withheldPartyRefusal: boolean;
  readonly silentStartEndSecond: number | null;
  readonly longestConversationMinute: number | null;
  /** What a change names in `If-Match`; `0` while nothing is saved. */
  readonly revision: number;
  /** `false` while caller protection is not available yet in this deploy: the values show, nothing can change. */
  readonly available: boolean;
  readonly updatedAt: string | null;
}

/** A change: every field stated, `null` is "off" (`CallerProtectionChange`). */
export interface CallerProtectionChange {
  readonly perHour: number | null;
  readonly perDay: number | null;
  readonly withheldPartyRefusal: boolean;
  readonly silentStartEndSecond: number | null;
  readonly longestConversationMinute: number | null;
}

/**
 * A refusal from a party route (caller protection, blocked numbers, the access list) with its detail:
 * `error.detail.code` names the broken rule of a `400 validation_failed` (`per_day_below_per_hour`, `number_invalid`,
 * `label_too_long`, …), `error.detail.field` its field.
 */
export class PartyRefusedError extends ApiError {
  readonly detailCode: string | null;
  readonly detailField: string | null;
  /** plan-029 WAPP-CONF: `error.detail.reason` (`method_unavailable`'s `sms_code_off` …), or `null`. */
  readonly detailReason: string | null;
  constructor(status: number, body: unknown, requestId: string | null) {
    super(status, body, requestId);
    const detail = (body as { error?: { detail?: unknown } } | null)?.error?.detail;
    const record = typeof detail === "object" && detail !== null ? (detail as Readonly<Record<string, unknown>>) : {};
    this.detailCode = typeof record["code"] === "string" ? record["code"] : null;
    this.detailField = typeof record["field"] === "string" ? record["field"] : null;
    this.detailReason = typeof record["reason"] === "string" ? record["reason"] : null;
  }
}

/** The body of a 2xx answer, or {@link PartyRefusedError} with the detail read. Never a partial result. */
export function partyBodyOf(result: ApiResult): unknown {
  if (result.status >= 200 && result.status < 300) return result.body;
  throw new PartyRefusedError(result.status, result.body, result.requestId);
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-029 API-PROT: the VoiceAgent's caller protection with its revision; every setting off at revision 0. */
    readVoiceAgentCallerProtection(voiceAgentId: string): Promise<CallerProtectionView>;
    /**
     * plan-029 API-PROT: save the caller protection, naming the revision read. Raises {@link PartyRefusedError}:
     * `validation_failed` with the rule, `caller_protection_unavailable`, `409 revision_stale`.
     */
    changeVoiceAgentCallerProtection(voiceAgentId: string, change: CallerProtectionChange, revision: number): Promise<CallerProtectionView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type CallerProtectionGatewaySlice = Pick<ConsoleGateway, "readVoiceAgentCallerProtection" | "changeVoiceAgentCallerProtection">;

/** This area's rows of `GATEWAY_GAP`: none — both operations are served. */
export const callerProtectionGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function nullableWhole(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

/** One `CallerProtection`. */
export function callerProtectionOf(wire: unknown, path: string): CallerProtectionView {
  const data = readRecord(wire, path);
  return {
    perHour: nullableWhole(data["per_hour"], `${path}.per_hour`),
    perDay: nullableWhole(data["per_day"], `${path}.per_day`),
    withheldPartyRefusal: readBoolean(data["withheld_party_refusal"], `${path}.withheld_party_refusal`),
    silentStartEndSecond: nullableWhole(data["silent_start_end_second"], `${path}.silent_start_end_second`),
    longestConversationMinute: nullableWhole(data["longest_conversation_minute"], `${path}.longest_conversation_minute`),
    revision: readWholeNumber(data["revision"], `${path}.revision`),
    available: readBoolean(data["available"], `${path}.available`),
    updatedAt: data["updated_at"] === null ? null : readText(data["updated_at"], `${path}.updated_at`),
  };
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

export function callerProtectionApiSlice(client: WeTalkApiClient, _context: SliceContext): CallerProtectionGatewaySlice {
  return {
    async readVoiceAgentCallerProtection(voiceAgentId) {
      const answer = await client.getVoiceAgentCallerProtection({ path: { voice_agent_id: voiceAgentId } });
      return callerProtectionOf(dataOf(partyBodyOf(answer), "getVoiceAgentCallerProtection"), "getVoiceAgentCallerProtection.data");
    },
    async changeVoiceAgentCallerProtection(voiceAgentId, change, revision) {
      const answer = await client.setVoiceAgentCallerProtection({
        path: { voice_agent_id: voiceAgentId },
        body: {
          per_hour: change.perHour,
          per_day: change.perDay,
          withheld_party_refusal: change.withheldPartyRefusal,
          silent_start_end_second: change.silentStartEndSecond,
          longest_conversation_minute: change.longestConversationMinute,
        },
        header: { "if-match": `"${String(revision)}"` },
      });
      return callerProtectionOf(dataOf(partyBodyOf(answer), "setVoiceAgentCallerProtection"), "setVoiceAgentCallerProtection.data");
    },
  };
}

// ── the read the panel uses ──────────────────────────────────────────────────────────────────────────────

/** Query keys of this area, per VoiceAgent. */
export const CALLER_PROTECTION_QUERY_KEY = {
  callerProtection: (voiceAgentId: string) => ["caller-protection", voiceAgentId] as const,
};

/** The VoiceAgent's caller protection. A refused or failed read leaves `data` undefined — never a remembered value. */
export function useVoiceAgentCallerProtection(voiceAgentId: string): UseQueryResult<CallerProtectionView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: CALLER_PROTECTION_QUERY_KEY.callerProtection(voiceAgentId),
    queryFn: () => gateway.readVoiceAgentCallerProtection(voiceAgentId),
    retry: false,
  });
}
